import { reloadLogs } from "./app-logs.ts";
import type { OpenClawApp } from "./app.ts";
import { shouldDeferChatHistoryReload } from "./chat/chat-history-defer.ts";
import { isSubagentRunActive } from "./chat/subagent-run-status.ts";
import { loadAgents } from "./controllers/agents.ts";
import {
  loadChatPalaceContinuity,
  PALACE_CONTINUITY_POLL_MIN_GAP_MS,
} from "./controllers/chat-palace-continuity.ts";
import { loadChatHistory } from "./controllers/chat.ts";
import { loadDebug } from "./controllers/debug.ts";
import { loadLiveSessions } from "./controllers/live-sessions.ts";
import { loadLocalResources } from "./controllers/llm-stats-capacity.ts";
import { refreshLocalLlm } from "./controllers/local-llm.ts";
import { loadNodes } from "./controllers/nodes.ts";
import { loadFleetStatus } from "./controllers/workforce-ui.ts";
import { isLocalLlmViewActive } from "./local-llm/view-active.ts";
import type { Tab } from "./navigation.ts";
import { isFleetConsoleTab } from "./navigation.ts";
import type { LocalResourcesSnapshot } from "./types.ts";
import type { FleetChatMainTabId } from "./views/fleet-chat-main-tabs.ts";

export const LOCAL_LLM_POLL_IDLE_MS = 10_000;
export const LOCAL_LLM_POLL_ACTIVE_MS = 1_000;
/** Idle Fleet console history/session refresh. */
export const CHAT_POLL_INTERVAL_MS = 5_000;
/** Faster refresh while a reply is in flight so the context ring stays current. */
export const CHAT_POLL_ACTIVE_INTERVAL_MS = 2_000;
/** Subagent registry poll when no cards are running. */
export const SUBAGENTS_POLL_IDLE_MS = 30_000;
/** Faster registry poll while parallel subagents are still running. */
export const SUBAGENTS_POLL_ACTIVE_MS = 5_000;
/** Sidebar session catalog refresh — coarser than chat.history to avoid UI jank. */
export const CHAT_SESSION_CATALOG_POLL_MS = 30_000;

type PollingHost = {
  nodesPollInterval: number | null;
  logsPollInterval: number | null;
  debugPollInterval: number | null;
  agentsPollInterval: number | null;
  subagentsPollInterval: number | null;
  liveSessionsPollInterval: number | null;
  llmStatsCapacityPollInterval: number | null;
  localLlmPollInterval: number | null;
  chatPollInterval: number | null;
  tab: Tab;
  connected: boolean;
  chatSending?: boolean;
  stream?: string | null;
  /** JAISIU-XXXX (chat-stops-processing-glitch): id of the in-flight
   *  agent run on the server, if any. Drives fast-poll cadence so
   *  a mid-run conversation never sits idle for >2s on the wire.
   *  `chatRunId` is the App-state field; we accept both names so
   *  existing call sites don't need rewiring. */
  runId?: string | null;
  chatRunId?: string | null;
  sessionBackgroundRuns?: Set<string>;
  sessionBackgroundRunIds?: string[];
  chatSubagents?: import("./ui-types.ts").ChatSubagentCard[];
  chatSync?: { lastEventAt?: number | null };
  localResourcesAutoRefresh: boolean;
  localLlmSnapshot?: LocalResourcesSnapshot | null;
  settings: { localLlmAutoRefresh: boolean; fleetChatMainTab: FleetChatMainTabId };
  sessionKey: string;
  /** JAISIU-XXXX — last time we polled Palace continuity (ms). Lets the
   *  poll cadence be coarser than the chat-history cadence (continuity
   *  changes less often than transcript). */
  chatPalaceContinuityLastPollAt?: number;
  /** JAISIU-XXXX — pause polling when the user has the dropdown open and
   *  is mid-edit on a draft. Otherwise we'd race the optimistic draft
   *  against the server snapshot. */
  chatPalaceContinuityOpen?: boolean;
  chatPalaceContinuityMutating?: boolean;
  chatPalaceContinuityPending?: Set<string>;
  /** Last sidebar catalog refresh from the chat poll tick (ms). */
  chatSessionCatalogLastPollAt?: number;
};

function resolveChatPollIntervalMs(host: PollingHost): number {
  if (host.chatSending || Boolean(host.stream?.trim())) {
    return CHAT_POLL_ACTIVE_INTERVAL_MS;
  }
  return CHAT_POLL_INTERVAL_MS;
}

function shouldSkipChatHistoryPoll(host: PollingHost): boolean {
  return shouldDeferChatHistoryReload(host);
}

function refreshChatPollSurfaces(host: PollingHost) {
  if (!shouldSkipChatHistoryPoll(host)) {
    void loadChatHistory(host as unknown as OpenClawApp, { quiet: true });
  }
  const settings = (host as { settings?: { fleetInspectorTab?: string } }).settings;
  if (host.connected && settings?.fleetInspectorTab === "health") {
    void loadFleetStatus(host as unknown as import("./app-view-state.ts").AppViewState, {
      quiet: true,
    });
  }
  // JAISIU-XXXX — Palace continuity poll. The dropdown can show stale
  // data between runs because the only refresh triggers are panel-open
  // and chat.final events. A background poll at ~2× the chat-history
  // interval keeps the badge count + structured snapshot honest without
  // hammering the gateway.
  //
  // Pause conditions:
  //   - disconnected (RPC will fail)
  //   - user has the dropdown open (avoid clobbering their draft)
  //   - a write RPC is in flight (the pending Set is non-empty)
  //   - the per-session key is empty (no session → no continuity)
  //   - we polled less than PALACE_CONTINUITY_POLL_MIN_GAP_MS ago
  const now = Date.now();
  const palaceSkip =
    !host.connected ||
    !host.sessionKey?.trim() ||
    host.chatPalaceContinuityOpen === true ||
    host.chatPalaceContinuityMutating === true ||
    (host.chatPalaceContinuityPending?.size ?? 0) > 0 ||
    now - (host.chatPalaceContinuityLastPollAt ?? 0) < PALACE_CONTINUITY_POLL_MIN_GAP_MS;
  if (!palaceSkip) {
    // Set the timestamp before the RPC so overlapping ticks while a request
    // is in-flight do not launch duplicate continuity probes.
    host.chatPalaceContinuityLastPollAt = now;
    void loadChatPalaceContinuity(host as unknown as OpenClawApp, { quiet: true });
  }
  // Sidebar catalog: refresh on a coarser cadence than chat.history.
  // Calling `sessions.list` every 2–5s with `sessionsLoading=true
  // forced full Lit re-renders and froze the chat UI during active runs.
  const catalogDue = now - (host.chatSessionCatalogLastPollAt ?? 0) >= CHAT_SESSION_CATALOG_POLL_MS;
  if (catalogDue) {
    host.chatSessionCatalogLastPollAt = now;
    void (host as unknown as OpenClawApp).refreshChatHistorySessions?.();
  }
}

export function resolveLocalLlmPollIntervalMs(host: PollingHost): number {
  const gateway = host.localLlmSnapshot?.gateway;
  if (!gateway) {
    return LOCAL_LLM_POLL_IDLE_MS;
  }
  const ollamaStreaming = gateway.inflightRuns.some(
    (run) => run.provider.trim().toLowerCase() === "ollama" && run.streaming,
  );
  if (gateway.streamingRuns > 0 || ollamaStreaming) {
    return LOCAL_LLM_POLL_ACTIVE_MS;
  }
  return LOCAL_LLM_POLL_IDLE_MS;
}

export function startNodesPolling(host: PollingHost) {
  if (host.nodesPollInterval != null) {
    return;
  }
  if (host.tab !== "instances") {
    return;
  }
  host.nodesPollInterval = window.setInterval(
    () => void loadNodes(host as unknown as OpenClawApp, { quiet: true }),
    5000,
  );
}

export function stopNodesPolling(host: PollingHost) {
  if (host.nodesPollInterval == null) {
    return;
  }
  clearInterval(host.nodesPollInterval);
  host.nodesPollInterval = null;
}

export function startLogsPolling(host: PollingHost) {
  if (host.logsPollInterval != null) {
    return;
  }
  host.logsPollInterval = window.setInterval(() => {
    if (host.tab !== "logs") {
      return;
    }
    void reloadLogs(host as unknown as OpenClawApp, { quiet: true });
  }, 2000);
}

export function stopLogsPolling(host: PollingHost) {
  if (host.logsPollInterval == null) {
    return;
  }
  clearInterval(host.logsPollInterval);
  host.logsPollInterval = null;
}

export function startDebugPolling(host: PollingHost) {
  if (host.debugPollInterval != null) {
    return;
  }
  host.debugPollInterval = window.setInterval(() => {
    if (host.tab !== "debug") {
      return;
    }
    void loadDebug(host as unknown as OpenClawApp);
  }, 3000);
}

export function stopDebugPolling(host: PollingHost) {
  if (host.debugPollInterval == null) {
    return;
  }
  clearInterval(host.debugPollInterval);
  host.debugPollInterval = null;
}

export function startAgentsPolling(host: PollingHost) {
  if (host.agentsPollInterval != null) {
    return;
  }
  // Self-gate: only useful while the Agents tab is mounted.
  if (host.tab !== "agents") {
    return;
  }
  host.agentsPollInterval = window.setInterval(
    () => void loadAgents(host as unknown as OpenClawApp, { quiet: true }),
    5000,
  );
}

export function stopAgentsPolling(host: PollingHost) {
  if (host.agentsPollInterval == null) {
    return;
  }
  clearInterval(host.agentsPollInterval);
  host.agentsPollInterval = null;
}

function subagentsHaveActiveCards(app: OpenClawApp): boolean {
  return (app.chatSubagents ?? []).some((card) => isSubagentRunActive(card.status));
}

export function resolveSubagentsPollDelayMs(host: PollingHost): number {
  const app = host as unknown as OpenClawApp;
  return subagentsHaveActiveCards(app) ? SUBAGENTS_POLL_ACTIVE_MS : SUBAGENTS_POLL_IDLE_MS;
}

/**
 * Monotonic generation for the subagent self-rescheduling poll.
 *
 * The previous chain used `if (host.subagentsPollInterval != null)` to decide
 * whether it was still the current chain. That check is wrong: the field
 * holds the NEWEST timer, not "is anyone still running". If
 * `stopSubagentsPolling()` fired while this chain was suspended on
 * `await refreshSubagentRegistry()` (a real WS RPC), it nulled the field and
 * cleared the handle; a tab switch then started a fresh chain; and this one
 * resumed, saw a non-null field, and rescheduled — overwriting the handle and
 * orphaning the newer timer, which fired and spawned another. Each
 * stop/start overlap leaked a permanently-live chain, so RPC traffic and
 * re-render pressure grew without bound.
 *
 * A generation counter makes the ownership question unambiguous: only the
 * chain that still holds the current generation may reschedule.
 */
let subagentsPollGeneration = 0;

function scheduleSubagentsPoll(host: PollingHost, generation: number): void {
  const delay = resolveSubagentsPollDelayMs(host);
  host.subagentsPollInterval = window.setTimeout(() => {
    void (async () => {
      // A stop (or a newer start) happened while this timer was pending.
      if (generation !== subagentsPollGeneration) {
        return;
      }
      const app = host as unknown as OpenClawApp;
      await app.refreshSubagentRegistry?.();
      // Re-check after the await: the generation may have advanced while
      // this chain was in flight.
      if (generation === subagentsPollGeneration) {
        scheduleSubagentsPoll(host, generation);
      }
    })();
  }, delay);
}

export function startSubagentsPolling(host: PollingHost) {
  if (host.subagentsPollInterval != null) {
    return;
  }
  // `isFleetConsoleTab` includes the chat tab itself (the chat
  // interface IS the fleet console). Limiting this to a non-chat
  // "fleet console" tab would silently disable the poll that paints
  // the chat-side subagent cards — the operator's activity bubble
  // froze for 30s+ on a multi-minute research run, reported as
  // "sessions don't appear to be doing anything".
  if (!isFleetConsoleTab(host.tab)) {
    return;
  }
  subagentsPollGeneration += 1;
  scheduleSubagentsPoll(host, subagentsPollGeneration);
}

export function stopSubagentsPolling(host: PollingHost) {
  // Invalidate first, so an in-flight chain cannot reschedule even if it has
  // not yet reached its post-await generation check.
  subagentsPollGeneration += 1;
  if (host.subagentsPollInterval == null) {
    return;
  }
  clearTimeout(host.subagentsPollInterval);
  host.subagentsPollInterval = null;
}

export function startLiveSessionsPolling(host: PollingHost) {
  if (host.liveSessionsPollInterval != null) {
    return;
  }
  // Self-gate: live sessions data is only useful on the Employees tab
  // (and its detail sub-routes). Without this gate, the 5s tick fires on
  // every page load including the chat tab, where the panel is hidden and
  // the rendered UI does not depend on it.
  if (host.tab !== "employees") {
    return;
  }
  host.liveSessionsPollInterval = window.setInterval(() => {
    if (!host.connected) {
      return;
    }
    void loadLiveSessions(host as unknown as OpenClawApp);
  }, 5000);
  // Fire once immediately so the inspector doesn't wait 5s for the first paint.
  if (host.connected) {
    void loadLiveSessions(host as unknown as OpenClawApp);
  }
}

export function stopLiveSessionsPolling(host: PollingHost) {
  if (host.liveSessionsPollInterval == null) {
    return;
  }
  clearInterval(host.liveSessionsPollInterval);
  host.liveSessionsPollInterval = null;
}

export function startLlmStatsCapacityPolling(host: PollingHost) {
  if (host.llmStatsCapacityPollInterval != null) {
    return;
  }
  host.llmStatsCapacityPollInterval = window.setInterval(() => {
    if ((host.tab !== "llm-stats" && host.tab !== "llm") || !host.localResourcesAutoRefresh) {
      return;
    }
    void loadLocalResources(host as unknown as OpenClawApp, { quiet: true });
  }, 60_000);
}

export function stopLlmStatsCapacityPolling(host: PollingHost) {
  if (host.llmStatsCapacityPollInterval == null) {
    return;
  }
  clearInterval(host.llmStatsCapacityPollInterval);
  host.llmStatsCapacityPollInterval = null;
}

export function startLocalLlmPolling(host: PollingHost) {
  stopLocalLlmPolling(host);
  if (!isLocalLlmViewActive(host)) {
    return;
  }
  const tick = () => {
    if (!isLocalLlmViewActive(host) || !host.settings.localLlmAutoRefresh) {
      return;
    }
    void refreshLocalLlm(host as unknown as OpenClawApp, { quiet: true }).finally(() => {
      syncLocalLlmPollInterval(host);
    });
  };
  host.localLlmPollInterval = window.setInterval(tick, resolveLocalLlmPollIntervalMs(host));
}

export function syncLocalLlmPollInterval(host: PollingHost): void {
  if (!isLocalLlmViewActive(host) || host.localLlmPollInterval == null) {
    return;
  }
  const nextMs = resolveLocalLlmPollIntervalMs(host);
  stopLocalLlmPolling(host);
  if (!host.settings.localLlmAutoRefresh) {
    return;
  }
  const tick = () => {
    if (!isLocalLlmViewActive(host) || !host.settings.localLlmAutoRefresh) {
      return;
    }
    void refreshLocalLlm(host as unknown as OpenClawApp, { quiet: true }).finally(() => {
      syncLocalLlmPollInterval(host);
    });
  };
  host.localLlmPollInterval = window.setInterval(tick, nextMs);
}

export function stopLocalLlmPolling(host: PollingHost) {
  if (host.localLlmPollInterval == null) {
    return;
  }
  clearInterval(host.localLlmPollInterval);
  host.localLlmPollInterval = null;
}

export function syncLocalLlmPolling(host: PollingHost): void {
  if (isLocalLlmViewActive(host)) {
    startLocalLlmPolling(host);
  } else {
    stopLocalLlmPolling(host);
  }
}

export function startChatPolling(host: PollingHost) {
  if (host.chatPollInterval != null) {
    return;
  }
  let lastRefreshAt = Number.NEGATIVE_INFINITY;
  host.chatPollInterval = window.setInterval(() => {
    if (!isFleetConsoleTab(host.tab) || !host.connected) {
      return;
    }
    const now = Date.now();
    if (now - lastRefreshAt < resolveChatPollIntervalMs(host)) {
      return;
    }
    lastRefreshAt = now;
    refreshChatPollSurfaces(host);
  }, CHAT_POLL_ACTIVE_INTERVAL_MS);
}

export function stopChatPolling(host: PollingHost) {
  if (host.chatPollInterval == null) {
    return;
  }
  clearInterval(host.chatPollInterval);
  host.chatPollInterval = null;
}

export function syncChatPolling(host: PollingHost): void {
  if (isFleetConsoleTab(host.tab) && host.connected) {
    startChatPolling(host);
  } else {
    stopChatPolling(host);
  }
}

// Queue polling stubs (queue tab not yet fully wired)
export function startQueuePolling(_host: PollingHost): void {
  // no-op — queue tab polling is driven by explicit load calls
}
export function stopQueuePolling(_host: PollingHost): void {
  // no-op
}
